import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileStack, Send } from 'lucide-react';
import { api } from '../../services/api';
import Badge from '../ui/Badge';
import { FormServerError } from '../ui/Field';
import { reviewTone, type ReviewState } from '../../utils/documentReviewModel';

/** FIX-D337-2 §5 (D-346 §6): what the customer gets at the delivery (no "מסמך מסירה"). */
const DELIVERY_ROLES = ['license', 'acceptance_checklist'];

/**
 * FIX-D337 stage ו (D-337 §6, §8) - the project page: "מסמכי האפיון" (the signed quote + annexes) and the internal
 * checklists for the delivery. Nothing when the project did not come from a package.
 * FIX-D337-2 §5: "מסירה ללקוח" - the license + the acceptance checklist go for signature (OTE manager, then the
 * customer by the personal link); their state is shown next to them.
 */
export default function ProjectCharacterizationDocs({ engagementId }: { engagementId: string | null }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['engagement-package', engagementId],
    queryFn: async () => (await api.getEngagementPackage(engagementId!)).data,
    enabled: !!engagementId,
  });
  const deliver = useMutation({
    mutationFn: (packageId: string) => api.sendPackageDelivery(packageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['engagement-package', engagementId] }),
  });
  const p = q.data;
  if (!p || !(p.documents ?? []).length) return null;
  const toCustomer = p.documents.filter((d: any) => d.customer_facing);
  const delivery = p.documents.filter((d: any) => !d.customer_facing);
  const toSend = delivery.filter((d: any) => DELIVERY_ROLES.includes(d.role) && (d.review_state === 'draft' || d.review_state === 'returned'));
  return (
    <section className="rounded-xl border border-surface-200 bg-surface-0 px-4 py-3" aria-label={t('taskSplit.projectDocs')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="flex items-center gap-1.5 font-medium text-surface-900"><FileStack size={16} aria-hidden="true" /> {t('taskSplit.projectDocs')}</span>
        {toCustomer.map((d: any) => <Link key={d.id} className="link" to={`/documents?doc=${d.id}`}>{d.title}</Link>)}
      </div>
      {delivery.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-surface-600">
          <span>{t('taskSplit.forDelivery')}</span>
          {delivery.map((d: any) => (
            <span key={d.id} className="inline-flex items-center gap-1.5">
              <Link className="link" to={`/documents?doc=${d.id}`}>{d.title}</Link>
              {DELIVERY_ROLES.includes(d.role) && d.review_state && d.review_state !== 'draft' && (
                <Badge tone={reviewTone(d.review_state as ReviewState)} dot>{t(`docReview.state.${d.review_state}`)}</Badge>
              )}
            </span>
          ))}
          {toSend.length > 0 && (
            <button type="button" className="btn btn-secondary btn-sm" disabled={deliver.isPending} onClick={() => deliver.mutate(p.id)}>
              <Send size={14} aria-hidden="true" /> {t('taskSplit.sendDelivery')}
            </button>
          )}
        </div>
      )}
      {toSend.length > 0 && <p className="mt-1 text-xs text-surface-500">{t('taskSplit.sendDeliveryHint')}</p>}
      <FormServerError error={deliver.error} fallback={t('docReview.actionError')} />
    </section>
  );
}
